import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { environment } from '../../../environments/environment'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'

export type SupportTicketCategory = string
export type SupportTicketStatus = 'open' | 'in_progress' | 'done' | 'closed'
export type SupportTicketPriority = 'low' | 'normal' | 'high'

export interface SupportCategory {
  code: string
  label: string
}

/** Fallback locale se il lookup non è raggiungibile (il backend usa gli stessi codici). */
export const DEFAULT_SUPPORT_CATEGORIES: SupportCategory[] = [
  { code: 'suggestion', label: 'Suggerimento' },
  { code: 'advice', label: 'Consiglio' },
  { code: 'bug', label: 'Bug / malfunzionamento' },
  { code: 'data-change', label: 'Modifica dati' },
  { code: 'other', label: 'Altro' }
]

/** Filtro lista gestione: 'active' = open + in_progress. */
export type SupportTicketFilter = SupportTicketStatus | 'active'

export type SupportTicketEventType = 'created' | 'status_changed' | 'assigned' | 'due_date_changed'

export interface SupportTicket {
  id: number
  title: string
  body: string
  category: SupportTicketCategory
  priority: SupportTicketPriority
  priorityRank: number
  status: SupportTicketStatus
  statusRank: number
  owner: string
  ownerName: string
  assignee: string | null
  assigneeName: string | null
  dueDate: string | null
  siteKey: string | null
  contextUrl: string | null
  appVersion: string | null
  userAgent: string | null
  resolution: string | null
  resolvedBy: string | null
  resolvedAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface SupportTicketEvent {
  id: number
  ticketId: number
  type: SupportTicketEventType
  fromValue: string | null
  toValue: string | null
  note: string | null
  changedBy: string | null
  changedByName: string | null
  createdAt: string | null
}

export interface SupportAssignee {
  code: string
  name: string
}

export interface SupportTicketCount {
  mine: number
  all: number
  canManage: boolean
}

export interface SupportTicketCreate {
  title: string
  body: string
  category: SupportTicketCategory
  priority: SupportTicketPriority
  contextUrl?: string | null
  appVersion?: string | null
  userAgent?: string | null
  siteKey?: string | null
}

export interface SupportTicketUpdate {
  status: SupportTicketStatus
  resolution: string | null
  assignee: string | null
  dueDate: string | null
}

@Injectable({ providedIn: 'root' })
export class SupportTicketApiService {
  private readonly http = inject(HttpClient)
  private readonly fuseqHeaders = inject(FuseqHeaders)

  getMine (): Observable<SupportTicket[]> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/mine`
    return this.http.get<SupportTicket[]>(apiEndPoint, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getCount (): Observable<SupportTicketCount> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/count`
    return this.http.get<SupportTicketCount>(apiEndPoint, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getCategories (): Observable<SupportCategory[]> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/categories`
    return this.http.get<SupportCategory[]>(apiEndPoint, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getAll (status?: SupportTicketFilter): Observable<SupportTicket[]> {
    const query = status ? `?status=${status}` : ''
    const apiEndPoint = `${environment.API_URL}/supportTickets/all${query}`
    return this.http.get<SupportTicket[]>(apiEndPoint, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getAssignees (): Observable<SupportAssignee[]> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/assignees`
    return this.http.get<SupportAssignee[]>(apiEndPoint, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getHistory (id: number): Observable<SupportTicketEvent[]> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/${id}/history`
    return this.http.get<SupportTicketEvent[]>(apiEndPoint, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  create (payload: SupportTicketCreate): Observable<SupportTicket> {
    const apiEndPoint = `${environment.API_URL}/supportTickets`
    return this.http.post<SupportTicket>(apiEndPoint, payload, this.fuseqHeaders.setTokenHeaders('POST'))
  }

  updateTicket (id: number, payload: SupportTicketUpdate): Observable<SupportTicket> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/${id}`
    return this.http.put<SupportTicket>(apiEndPoint, payload, this.fuseqHeaders.setTokenHeaders('PUT'))
  }

  remove (id: number): Observable<any> {
    const apiEndPoint = `${environment.API_URL}/supportTickets/${id}`
    return this.http.delete(apiEndPoint, this.fuseqHeaders.setTokenHeaders('DELETE'))
  }
}
